import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
  UploadControlContextProvider,
} from '@sw-sw/lib-form';
import { maxBy } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { InspectionContext } from '../../../contexts/InspectionContext';
import { RolesContext } from '../../../contexts/RolesContext';
import { dateFormat } from '../../../utils';
import FormModal from '../../Shared/form/modal/FormModal';
import {
  defaultCompletionTemplateContent,
  getFindingCommentsSchema,
} from './FindingsForm';
import uploadApi from '../../../utils/api/upload';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import EXIF from 'exif-js';
import { dmsToDecimal, getCurrentPosition, showLocationOnMap } from '../../../utils/googlemaps';
import { ProjectContext } from '../../../contexts/ProjectContext';
import TenantFeatureContext from '../../../contexts/TenantContext';
import clientApi from '../../../utils/api/client';
import { Modal } from '@sw-sw/lib-ui';
import { toast } from 'react-toastify';
import { getCurrentDateTime } from '../../../utils/date';
import AppContext from '../../../contexts/AppContext';

const getCompleteFindingSchema = (findings, permCheck, isGuest, isFindingBackdatingDisabled) => {
  const minValidationDate = maxBy(
    findings.map((finding) => new Date(finding.date_initiated)),
  );
  const schema = {
    finding_ids: {
      label: `Selected Findings`,
      controlType: UIControlType.custom,
      renderControl: ({ value }) => (
        <ul className='form-control'>
          {value.map((id) => {
            const { number: num, date_initiated: date } = findings.find(
              (f) => f.id === id,
            );

            return (
              <li key={id}>
                #{num} on {moment.utc(date).format(dateFormat)}
              </li>
            );
          })}
        </ul>
      ),
    },
    date_completed: {
      label: 'Date Completed',
      controlType: UIControlType.date,
      validation: {
        minDate: isFindingBackdatingDisabled ? new Date(): minValidationDate,
        maxDate: new Date(),
        required: true,
      }
    },
    completed_by: {
      label: 'Completed By',
      controlType: UIControlType.text,
    },

    // comment creation only
    ...getFindingCommentsSchema(permCheck('create', 'finding_comments'), false),
  };

  if(isGuest){
    schema.date_completed.style = { flex: '1 1 50%' };
    schema.completed_by.style = { flex: '1 1 50%' };
  }else{
    delete schema.completed_by;
  }

  if (permCheck('create', 'Finding Images')) {
    findings.forEach(
      (f) =>
        (schema[`images_${f.id}`] = {
          label: `#${f.number} on ${moment
            .utc(f.date_initiated)
            .format(dateFormat)}`,
          controlType: UIControlType.imageUpload,
        }),
    );
  }

  return schema;
};

const CompleteFindingsModalUI = ({
  findings,
  onSubmit,
  onClose,
  isEnabledPhotoMetadata,
  isFindingBackdatingDisabled
}) => {
  const formContext = useContext(FormContext);
  const roleContext = useContext(RolesContext);
  const appStore = useContext(AppContext);
  const history = useHistory();
  const user = appStore.get("user");
  const isGuest = history.location.pathname.startsWith("/guest") && user?.roleName === "Guest";

  useEffect(() => {
    if (!isEnabledPhotoMetadata) return;
    const imagesList = Object.keys(formContext.value).filter((key) =>
      key.startsWith('images_'),
    );

    imagesList.forEach((key) => {
      const currentImages = [...(formContext.value[key] || [])];

      currentImages.forEach((image, index) => {
        if (image.file && image.gpsData === undefined) {
          EXIF.getData(image.file, function () {
            const gpsLat = EXIF.getTag(this, 'GPSLatitude');
            const gpsLon = EXIF.getTag(this, 'GPSLongitude');
            const gpsLatRef = EXIF.getTag(this, 'GPSLatitudeRef');
            const gpsLonRef = EXIF.getTag(this, 'GPSLongitudeRef');
            const timestamp = EXIF.getTag(this, 'DateTime');
            const capturedInLessThan2Minutes =
              image.file.lastModified &&
              Date.now() - image.file.lastModified < 2 * 60 * 1000;
  
            const updateImage = (newData) => {
              currentImages[index] = {
                ...currentImages[index],
                ...newData,
              };
              formContext.set(key, [...currentImages]);
            };
  
            if (gpsLat && gpsLon) {
              const lat = dmsToDecimal(gpsLat, gpsLatRef);
              const lon = dmsToDecimal(gpsLon, gpsLonRef);
              updateImage({
                gpsData: { lat, lon },
                gps_latitude: lat,
                gps_longitude: lon,
                timestamp: timestamp || null,
              });
            } else if (!gpsLat && !gpsLon && capturedInLessThan2Minutes) {
              getCurrentPosition().then(({ latitude, longitude }) => {
                updateImage({
                  gpsData: { lat: latitude, lon: longitude },
                  gps_latitude: latitude,
                  gps_longitude: longitude,
                  timestamp: getCurrentDateTime(),
                });
              });
            } else {
              updateImage({
                gpsData: null,
                gps_latitude: null,
                gps_longitude: null,
                timestamp: null,
              });
            }
          });
        }
      });
    });  
  }, [findings, formContext.value]);

  return (
    <FormModal
      modalProps={{
        title: 'Complete Findings',
        isExtraWide: true,
        classes: 'complete-findings',
      }}
      submitText='Complete'
      onCancel={onClose}
      onSubmit={onSubmit}
      dismissOnEsc={false}
    >
      <UploadControlContextProvider
        allowAnnotation={roleContext.userHasPermission('all', 'Annotations')}
        allowDelete
        allowCreate
        allowGeolocation={isEnabledPhotoMetadata}
        getMapUrl={showLocationOnMap}
        getUrl={uploadApi.getDownloadUrl}
      >
        <section className='complete-findings-form'>
          <FormSchemaFields
            schema={getCompleteFindingSchema(
              findings,
              roleContext.userHasPermission,
              isGuest,
              isFindingBackdatingDisabled
            )}
            onChange={formContext.set}
            formData={formContext.value}
          />
        </section>
      </UploadControlContextProvider>
    </FormModal>
  );
};

CompleteFindingsModalUI.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  findings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  isEnabledPhotoMetadata: PropTypes.bool.isRequired,
};

const CompleteFindingsModal = ({ findings: findingIds, onSubmit, onClose }) => {
  const inspectionContext = useContext(InspectionContext);
  const projectContext = useContext(ProjectContext);
  const tenantFeatureContext = useContext(TenantFeatureContext);

  const project = projectContext.project;
  const [isEnabledPhotoMetadata, setIsEnabledPhotoMetadata] = useState(false);
  const [noMetadataImage, setNoMetadataImage] = useState([]);
  const [showMetadataWarning, setShowMetadataWarning] = useState(false);

  const appStore = useContext(AppContext);
  const history = useHistory();
  const user = appStore.get("user");
  const isGuest = history.location.pathname.startsWith("/guest") && user?.roleName === "Guest";

  useEffect(() => {
    (async () => {
      if (project) {
        if (project.client_id) {
          const c = await clientApi.getCustomFields(project.client_id, [
            'finding_metric_tracking',
             'disable_images',
            'enable_photo_metadata_requirement',
          ]);

          if (c && c.enable_photo_metadata_requirement) {
            setIsEnabledPhotoMetadata(
              c.enable_photo_metadata_requirement &&
                tenantFeatureContext.tenantHasFeature(
                  'Enable Photo Metadata Requirement',
                ) &&
                Boolean(project.enable_photo_metadata_requirement),
            );
          }
        }
      }
    })();
  }, [project]);

  const handleSubmitClickNoMetadata = (formData) => {

    if (isGuest && ((formData.date_completed && !formData.completed_by) || (!formData.date_completed && formData.completed_by))) {
      toast.error(`Please fill 'Completed By' field with your name along with 'Date Completed' to mark finding as complete`);
      return false;
    }

    const imgFormData = Object.keys(formData).filter((key) =>
      key.startsWith('images_'),
    );

    let noMetaImage = [];

    imgFormData.forEach((key) => {
      const images = formData[key];

      images.forEach((image) => {
        if (image.gps_latitude === null || image.gps_longitude === null) {
          noMetaImage.push(image);
        }
      });
    });

    if (noMetaImage.length > 0 && isEnabledPhotoMetadata) {
      setNoMetadataImage(noMetaImage);
      setShowMetadataWarning(true);
    } else {
      const toastId = toast.loading('Completing findings...');
      inspectionContext.completeFindings(formData).then(() => {
        toast.update(toastId, {
          render: 'Findings completed successfully',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
        onSubmit(formData);
      });
    }
  };

  const findings = inspectionContext.findings.filter(({ id }) =>
    findingIds.includes(id),
  );

  /** @todo initial value */
  const initialValue = useRef({
    finding_ids: findings.map((_) => _.id),
    comments: defaultCompletionTemplateContent,
    date_completed: new Date(),
  });

  return (
    <FormContextProvider initialValue={initialValue.current}>
      <CompleteFindingsModalUI
        findings={findings}
        onClose={onClose}
        onSubmit={(formData) => handleSubmitClickNoMetadata(formData)}
        isEnabledPhotoMetadata={isEnabledPhotoMetadata}
        isFindingBackdatingDisabled={inspectionContext.isFindingBackdatingDisabled}
      />
      <Modal
        show={showMetadataWarning}
        title='Warning'
        subTitle={`Please remove images that do not contain the required metadata to complete these findings.`}
        cancelBtn={false}
        submitBtnText='Ok'
        handleClose={() => setShowMetadataWarning(false)}
        handleSubmit={() => setShowMetadataWarning(false)}
      >
        <div>
          <ol>
            {noMetadataImage.map((image, index) => (
              <li key={index}>{image.name}</li>
            ))}
          </ol>
        </div>
      </Modal>
    </FormContextProvider>
  );
};

CompleteFindingsModal.propTypes = {
  findings: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CompleteFindingsModal;
